
import { Component, OnInit, input, signal, inject, DestroyRef, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { LorenAssetGalleryPhoto } from "../../../loren-assets/interfaces/loren-asset-gallery-photo.interface";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { LorenAssetDetailsPhotosDialogComponent } from "../loren-asset-details-photos-dialog/loren-asset-details-photos-dialog.component";
import { LorenAssetDetailsUploadPhotoDialogComponent } from "../loren-asset-details-upload-photo-dialog/loren-asset-details-upload-photo-dialog.component";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";

@Component({
  selector: "loren-asset-details-photos",
  imports: [
    MatIcon,
    MatProgressSpinner,
    TranslocoPipe,
    LorenDateModule,
    MatButton
],
  templateUrl: "./loren-asset-details-photos.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./loren-asset-details-photos.component.scss",
})
export class LorenAssetDetailsPhotosComponent implements OnInit {
  private matDialog = inject(MatDialog);
  private translate = inject(TranslocoService);
  private assetService = inject(LorenAssetService);
  private snackbarService = inject(LorenSnackBarService);

  private readonly destroyRef = inject(DestroyRef);
  asset = input.required<LorenAsset>();
  isHandset = input<boolean>();
  assetPhotos = signal<LorenAssetGalleryPhoto[]>(null);
  loading = signal<boolean>(false);
  error = signal<boolean>(false);

  ngOnInit() {
    this.getData();
  }

  openDialog(photo: number) {
    const dialogRef = this.matDialog.open(
      LorenAssetDetailsPhotosDialogComponent,
      {
        width: this.isHandset() ? "80%" : "50%",
        height: "95%",
        data: {
          assetName: this.asset().name,
          allPhotos: this.assetPhotos(),
          currentPhoto: photo,
        },
      }
    );
    return dialogRef.afterClosed();
  }

  openUploadDialog() {
    const dialogRef = this.matDialog.open(
      LorenAssetDetailsUploadPhotoDialogComponent,
      {
        width: this.isHandset() ? "80%" : "40%",
        height: "80%",
        data: {
          assetName: this.asset().name,
        },
      }
    );
    dialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res) => {
      if (!res) return;

      this.loading.set(true);
      const assetPhotoObj = {
        assetCondition: res.assetCondition,
        comment: res.comment,
        photo: res.photo.file,
      };
      this.assetService
        .addAssetPhoto(this.asset().id, assetPhotoObj)
        .pipe(
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => this.getData(),
          error: () =>
            this.snackbarService.showErrorMessage(
              this.translate.translate("error_page.general_error_message")
            ),
        });
    });
  }

  private getData() {
    this.loading.set(true);
    this.assetService.getAssetPhotos(this.asset().id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res: { id: number; photos: LorenAssetGalleryPhoto[] }) => {
        this.error.set(false);
        this.assetPhotos.set(res.photos);
        this.loading.set(false);
      },
      error: () => this.error.set(true),
    });
  }
}
